import type { DynamicMetadataType } from '@/modules/SEO/http/server/getMetadataProps';

const ONE_SECOND_IN_MS = 1000;

const isDevelopment = process.env.NODE_ENV === 'development';

export const apiConfig = {
  baseURL: process.env.NEXT_LOCAL_API_URL
    ? process.env.NEXT_LOCAL_API_URL
    : (process.env.NEXT_PUBLIC_API_URL ?? ''),
  comparatorBaseURL: process.env.NEXT_PUBLIC_COMPARATOR_API_URL ?? '',
  fpBaseURL: process.env.NEXT_PUBLIC_AIO_API_URL ?? '',
  nextBaseURL: isDevelopment
    ? (process.env.NEXT_LOCAL_APP_URL ?? '')
    : (process.env.NEXT_PUBLIC_APP_URL ?? ''),
  sseBaseURL: process.env.NEXT_PUBLIC_API_SSE_URL ?? '',
  timeout: ONE_SECOND_IN_MS * 15,
  validatorBaseUrl: process.env.NEXT_PUBLIC_API_VALIDATOR ?? '',
  routes: {
    assets: {
      list: '/app/v1/bff/v2/assets',
      recommendation: '/app/v1/bff/assets_all',
      opportunities: '/app/v1/bff/v2/opportunities',
      opportunitiesPublic: '/app/v1/public/bff/v2/opportunities',
    },
    auth: {
      account: '/auth/v1/account',
      checkIsCustomer: '/auth/v1/verify-login',
      experiences: '/app/v1/public/experiences',
      me: '/app/v1/me',
      resetPassword: '/auth/v1/reset-password',
      refreshToken: '/auth/v1/refresh-token',
      replies: '/app/v1/replies',
      sendOTP: '/auth/v1/send-otp',
      signIn: '/auth/v1/login/password',
      updateEmail: '/auth/v1/account/email',
      updatePhone: '/auth/v1/account/phone',
      updatePassword: '/auth/v1/account/password',
      validateOTP: '/auth/v1/validate-otp',
      verifyEmail: '/auth/v1/account/verify-email',
      verifyPhone: '/auth/v1/account/verify-phone',
      verifyOTP: '/auth/v1/login/otp',
    },
    author: {
      byId: (id: string) => `app/v1/bff/v2/authors/${id}`,
      publicById: (id: string) => `app/v1/public/bff/v2/authors/${id}`,
      programs: (id: string) => `app/v1/bff/v2/authors/programs/${id}`,
      publicPrograms: (id: string) =>
        `app/v1/public/bff/v2/authors/programs/${id}`,
    },
    banking: {
      pageContent: (page: string) => `app/v1/bff/v2/pages/${page}`,
      'live-off-passive-income': 'app/v1/public/bff/v2/live-off-interest',
    },
    bookmark: {
      delete: (uuid: string, type: string) =>
        `/app/v1/bff/v2/bookmark/${uuid}/${type}`,
      deleteMany: 'app/v1/bff/v2/bookmark/delete_many',
      list: (page: number, limit: number) =>
        `/app/v1/bff/v2/bookmark/${page}/${limit}`,
      save: '/app/v1/bff/v2/bookmark',
    },
    comparator: {
      assets: {
        rentabilityByPeriod: (id: string): string =>
          `/assets/${id}/rentability/period`,
      },
      chart: {
        rentability: (id: string) => `/assets/${id}/rentability/chart`,
      },
      auth: '/auth/token',
      local: {
        rentabilityPeriod: (id: string): string =>
          `/comparador/rentability-period/${id}`,
        rentabilityChart: (id: string): string =>
          `/comparador/rentability-period/${id}/chart`,
      },
    },
    wallet: {
      alerts: '/app/v1/public/bff/alerts',
      summary: '/app/v1/wallet/summary',
      connectors: '/app/v1/wallet/connectors',
      customer: {
        walletUpdate: '/wallet/v1/customer/wallet-update',
      },
      institutions: '/app/v1/wallet/institutions',
      investments: '/app/v1/wallet/my-investments',
      investmentsCategories: '/app/v1/wallet/my-investments/categories',
      investmentsInstitutions: '/app/v1/wallet/my-investments/institutions',
      suggested: {
        investments: '/app/v1/wallet/suggested-investments',
        investmentsCategories:
          '/app/v1/wallet/suggested-investments/categories',
      },
      optimizer: '/app/v1/wallet/optimizer',
      optimizerStatus: '/app/v1/wallet/optimizer/status',
      pluggy: {
        item: (item_id: string) =>
          `/app/v1/wallet/connect/pluggy/items/${item_id}`,
        items: '/app/v1/wallet/connect/pluggy/items',
        token: '/app/v1/wallet/connect/pluggy/token',
      },
    },
    content: {
      find: (slug: string) => `app/v1/bff/v2/contents/${slug}`,
      public: (slug: string) => `app/v1/public/bff/v2/contents/${slug}`,
      cta: (slug: string) => `app/v1/contents/${slug}/cta`,
    },
    crossSell: {
      highlights: '/app/v1/bff/v2/home/eqiplus/highlight_main',
      highlightsPublic: '/app/v1/public/bff/v2/home/eqiplus/highlight_main',
    },
    cta: (slug: string) => `app/v1/ctas/${slug}`,
    customer: {
      advisor: '/wallet/v1/customer/advisors',
      delete: '/app/v1/bff/customer/forget',
      privatePension: {
        assets: '/wallet/v1/chart/assets',
        invoices: '/wallet/v1/private-pension/boletos',
        position: '/wallet/v1/private-pension/position',
        transactions: '/wallet/v1/private-pension/transactions',
        incomeReportDownload: '/wallet/v1/customer/income-report-download',
      },
      sendVisit: `/app/v1/bff/v2/view-registers/visit`,
      sendVisitPublic: `/app/v1/public/bff/v2/view-registers/visit`,
      sendClick: `/app/v1/bff/v2/view-registers/click`,
      sendClickPublic: `/app/v1/public/bff/v2/view-registers/click`,
      sendEmailLead: '/app/v1/public/leads',
      sendXcode: `/app/v1/bff/customer/xcode`,
      sendDevice: `/app/v1/bff/v2/customer/device`,
      sendDevicePublic: `/app/v1/public/bff/v2/customer/device`,
      subscriptions: (uuid: string) => `app/v1/subscriptions/${uuid}`,
      wallet: {
        accounts: '/wallet/v1/customer/accounts',
        chart: {
          positionAssets: '/wallet/v1/chart/position-assets',
          subCategories: '/wallet/v1/chart/subCategories',
        },
        eqiWalletPerformance: '/wallet/v1/customer/wallet-performance',
        eqiWalletIndexesPerformance:
          '/app/v1/bff/v2/market-indexes/wallet-performance',
        lastContributions: '/wallet/v1/customer/last-contributions',
        monthPerformance: (yearMonth: string) =>
          `/wallet/v1/customer/performance/${yearMonth}`,
        monthContribuition: (accountId: string) =>
          `/wallet/v1/customer/account/${accountId}/month-contribution`,
        performance: '/wallet/v1/customer/portfolio-performance',
        indicatorsPerformance: '/app/v1/bff/v2/market-indexes/performance',
        status: '/wallet/v1/customer/status',
        walletPerformance: (id: string) =>
          `/wallet/v2/customer/wallet-performance?accountId=${id}`,
      },
    },
    entertainment: {
      content: {
        byType: (type: string) => `app/v1/bff/v2/home/${type}`,
        keepWatching: '/app/v1/bff/v2/home/eqiplus/keep_watch',
        lastViews: '/app/v1/bff/v2/home/eqiplus/last_contents',
        lastViewsPublic: '/app/v1/public/bff/v2/home/eqiplus/last_contents',
        watchTime: 'app/v1/watch',
        watchTimePublic: 'app/v1/public/watch',
      },
      history: {
        delete: 'app/v1/bff/v2/customer/historic/delete_many',
        list: (page: number, limit: number) =>
          `app/v1/bff/v2/customer/historic/${page}/${limit}`,
      },
      program: {
        v1: {
          contents: (id: string) => `app/v1/bff/v2/programs/contents/${id}`,
          publicContents: (id: string) =>
            `app/v1/public/bff/v2/programs/contents/${id}`,
          keepWatching: 'app/v1/bff/v2/programs/watch_recently',
          info: (id: string) => `app/v1/bff/v2/programs/${id}`,
          publicInfo: (id: string) => `app/v1/public/bff/v2/programs/${id}`,
          seasons: (id: string) => `app/v1/bff/v2/programs/seasons/${id}`,
          publicSeasons: (id: string) =>
            `app/v1/public/bff/v2/programs/seasons/${id}`,
        },
        contents: (uuid: string) =>
          `/app/v1/bff/v2/programs/eqiplus/${uuid}/contents`,
        files: (uuid: string) =>
          `/app/v1/bff/v2/programs/eqiplus/${uuid}/files`,
        publicFiles: (uuid: string) =>
          `/app/v1/public/bff/v2/programs/eqiplus/${uuid}/files`,
        info: (uuid: string) => `/app/v1/bff/v2/programs/eqiplus/${uuid}`,
        publicInfo: (uuid: string) =>
          `/app/v1/public/bff/v2/programs/eqiplus/${uuid}`,
        portfolio: (uuid: string) =>
          `/app/v1/bff/v2/programs/eqiplus/${uuid}/portfolio`,
        publicPortfolio: (uuid: string) =>
          `/app/v1/public/bff/v2/programs/eqiplus/${uuid}/portfolio`,
        relatedContents: (uuid: string) =>
          `/app/v1/bff/v2/programs/eqiplus/${uuid}/related-contents`,
        publicRelatedContents: (uuid: string) =>
          `/app/v1/public/bff/v2/programs/eqiplus/${uuid}/related-contents`,
        seasons: (uuid: string, isRestricted: boolean) =>
          `/app/v1/bff/v2/programs/eqiplus/${uuid}/seasons${isRestricted ? '?restricted=true' : ''}`,
        publicSeasons: (uuid: string) =>
          `/app/v1/public/bff/v2/programs/eqiplus/${uuid}/seasons`,
        tabs: (uuid: string) => `/app/v1/bff/v2/tabs/${uuid}`,
        cta: (slug: string) => `app/v1/programs/${slug}/cta`,
      },
    },
    events: {
      list: 'app/v1/bff/v2/calendar-events',
      listNexts: 'app/v1/bff/v2/calendar-events/nexts',
      publicListNexts: 'app/v1/public/bff/v2/calendar-events/nexts',
      listPasts: 'app/v1/bff/v2/calendar-events/pasts',
      publicListPasts: 'app/v1/public/bff/v2/calendar-events/pasts',
      confirmPresence: (uuid: string) =>
        `app/v1/bff/v2/calendar-events/${uuid}/mark`,
    },
    financialPlanning: {
      planners: '/fp/v1/planners',
      hasActivePlanner: '/fp/v1/planners/has-active',
      availableAssets: '/fp/v1/goals/available-assets',
      portfolioPosition: '/portfolio/me/global-auc',
      retirement: {
        all: '/fp/v1/retirements',
        byId: (id: number) => `/fp/v1/retirements/${id}`,
        defaultValues: '/fp/v1/planners/default-values',
      },
      wealthEvolutions: '/portfolio/me/wealth-evolutions',
      patrimonialProtections: `/fp/v1/patrimonial-protections`,
      immobilized: {
        addImmobilized: '/fp/v1/investments/non-financial',
        categories: '/fp/v1/categories',
        categoryItems: (id: number) => `/fp/v1/investments/categories/${id}`,
        categoryItemsBySlug: (slug: string) =>
          `/fp/v1/investments/categories/find-by-slug/${slug}`,
        deleteImmobilized: (id: string) =>
          `/fp/v1/investments/non-financial/${id}`,
        investimentsCategories: '/fp/v1/investments/categories',
        itemDetails: (id: number) => `/fp/v1/investments/non-financial/${id}`,
        updateImmobilized: (id: number) =>
          `/fp/v1/investments/non-financial/${id}`,
      },
      creditCards: {
        all: '/fp/v1/credit-cards',
        byId: (id: number) => `/fp/v1/credit-cards/${id}`,
        flags: '/fp/v1/credit-card-flags',
      },
      financialControl: {
        transactions: {
          all: `/fp/v1/transactions`,
          chart: (yearMonth: string) =>
            `/fp/v1/transactions/chart?yearMonth=${yearMonth}`,
          list: `/fp/v1/transactions`,
          byId: (id: string | number) => `/fp/v1/transactions/${id}`,
          categories: `/fp/v1/transaction/categories`,
          creditCards: {
            all: (yearMonth: string) =>
              `/fp/v1/credit-cards/transactions?yearMonth=${yearMonth}`,
            byId: (id: number, yearMonth: string) =>
              `/fp/v1/credit-cards/${id}/transactions?yearMonth=${yearMonth}`,
          },
        },
        transactionsV2: {
          all: `/fp/v2/transactions`,
          chart: `/fp/v2/transactions/chart`,
          byId: (id: number | string) => `/fp/v2/transactions/${id}`,
        },
        paymentMethods: `/fp/v1/payment-methods`,
      },
      goals: {
        goals: `/fp/v1/goals`,
        details: (id: string) => `/fp/v1/goals/${id}`,
        update: (id: string) => `/fp/v1/goals/${id}`,
        delete: (id: string) => `/fp/v1/goals/${id}`,
        performance: (id: string) => `/fp/v1/goals/${id}/performance`,
      },
      spendLimit: {
        updateCategorieLimits: (id: string) =>
          `/fp/v1/transaction/categories/${id}/limit`,
        subCategoriesLimit: (id: string) =>
          `/fp/v1/transaction/categories/${id}/subcategories/limit`,
        limits: '/fp/v1/transaction/categories/limit',
      },
      permissions:
        'https://s3.us-east-2.amazonaws.com/cdn.eqi.com.br/fp-customers-permissions.json',
      report: {
        suggestions: '/fp/v1/suggestions',
      },
    },
    gamification: {
      balance: '/app/v1/bff/v2/gamefication/balance',
      credentials: '/app/v1/bff/v2/gamefication/tokens',
      optin: '/app/v1/bff/v2/gamefication/optin',
      rewards: '/app/v1/bff/v2/gamefication/rewards',
    },
    home: {
      highlights: '/app/v1/bff/v2/home/eqiplus/highlights',
      highlightsPublic: '/app/v1/public/bff/v2/home/eqiplus/highlights',
    },
    invest: {
      brokers: '/app/v1/bff/brokers',
    },
    menu: {
      home: '/app/v1/bff/v2/menu-versions/home',
      getMenu: '/app/v1/bff/v3/menu-versions/menu-web',
      getMenuPublic: '/app/v1/public/bff/v3/menu-versions/menu-web',
    },
    news: {
      all: '/app/v1/public/bff/portal_eqi_noticias',
      last: '/app/v1/public/bff/portal_eqi_noticias/lasts',
      highlights: '/app/v1/public/bff/portal_eqi_noticias/highlights',
      viewOneNews: (slug: string) => `/app/v1/bff/portal_eqi_noticias/${slug}`,
      viewOneNewsPublic: (slug: string) =>
        `/app/v1/public/bff/portal_eqi_noticias/${slug}`,
      newsRelated: (slug: string) =>
        `/app/v1/public/bff/portal_eqi_noticias/${slug}/related`,
      feedRv: '/app/v1/rv/noticias',
      streamFeedRv: '/app/v1/rv/stream/noticias',
    },
    notifications: 'app/v1/bff/notifications',
    personalization: {
      MCPLogin: 'app/v1/mcp/login',
      MCPEvents: 'app/v1/mcp/event',
      MCPBannerInteraction: 'app/v1/mcp/banner/interaction',
    },
    research: {
      authors: '/app/v1/bff/v2/home/eqiplus/research/authors',
      authorsPublic: '/app/v1/public/bff/v2/home/eqiplus/research/authors',
      keepWatch: '/app/v1/bff/v2/home/eqiplus/research/keep-watch',
      highlights: '/app/v1/bff/v2/home/eqiplus/research/highlights',
      highlightsPublic:
        '/app/v1/public/bff/v2/home/eqiplus/research/highlights',
      topContents: '/app/v1/bff/v2/home/eqiplus/research/top-contents',
      topContentsPublic:
        '/app/v1/public/bff/v2/home/eqiplus/research/top-contents',
      marketMovers: '/app/v1/public/rv/market/movers',
    },
    retrospective: {
      stories: '/app/v1/bff/v2/home/eqiplus/retrospective',
    },
    salesForce: {
      customerAction: '/app/v1/bff/customer/salesforce/queue',
    },
    search: {
      assets: '/app/v1/bff/v2/search/assets',
      contents: '/app/v1/bff/v2/search/contents',
      portfolios: '/app/v1/bff/v2/search/portfolios',
      programs: `/app/v1/bff/v2/search/programs`,
      top: '/app/v1/bff/v2/search/top',
    },
    seo: (type: DynamicMetadataType, id: string) =>
      `app/v1/public/bff/v2/${type}/${id}/seo`,
    suggestion: {
      sendSuggestion: 'app/v1/bff/v2/suggestions',
    },
    walletPosition: {
      chart: {
        assets: '/wallet/v1/chart/assets',
        positionAssets: '/wallet/v1/chart/position-assets',
        subCategories: '/wallet/v1/chart/subCategories',
      },
      customer: {
        advisor: '/wallet/v1/customer/advisors',
        lastContributions: '/wallet/v1/customer/last-contributions',
        performance: '/wallet/v1/customer/performance',
        walletPerformance: '/wallet/v1/customer/performance/wallet',
        wealthEvolution: '/wallet/v1/customer/wealth-evolution',
      },
      privatePension: {
        invoices: '/wallet/v1/private-pension/boletos',
        position: '/wallet/v1/private-pension/position',
        transactions: '/wallet/v1/private-pension/transactions',
        incomeReportDownload: '/wallet/v1/customer/income-report-download',
        customerBeneficiaries: '/wallet/v1/customer/beneficiaries',
      },
    },
    secondaries: {
      cetipados: '/app/v1/bff/v2/home/eqiplus/solicitations/cetipados',
      intermadiations:
        '/app/v1/bff/v2/home/eqiplus/solicitations/intermediations',
    },
    liveStream: {
      rest: {
        fetchStatus: 'app/v1/rv/live_status',
        participants: {
          accept: (liveId: string): string =>
            `app/v1/rv/stream/live/${liveId}/accept`,
          leave: (liveId: string): string =>
            `app/v1/rv/stream/live/${liveId}/participants/me`,
        },
        recordings: '/app/v1/rv/live/recordings',
        token: '/stream/token',
        trial: '/app/v1/rv/trial',
      },
      sse: {
        watchStatus: 'sse/v1/rv/stream/live',
      },
      economicCalendar: 'app/v1/bff/v2/live/economic-calendar',
    },
    myFinances: {
      eqiWallet: {
        categories: (id: string) =>
          `/wallet/v1/account/portfolio/categories?accountId=${id}`,
        evolution: (id: string) =>
          `/wallet/v1/account/portfolio/evolution?accountId=${id}`,
        investments: (id: string) =>
          `/wallet/v1/account/portfolio/investments?accountId=${id}`,
        suggested: (id: string) =>
          `/wallet/v1/account/portfolio/suggested?accountId=${id}`,
      },
      openFinance: {
        dashboard: '/app/v1/wallet/open-finance/dashboard',
      },
      analysis: {
        comparison: (id: string, yearMonth: string) =>
          `/wallet/v1/customer/portfolio-comparison?accountId=${id}&yearMonth=${yearMonth}`,
        marketIndicators: '/wallet/v1/customer/market-indicators',
      },
    },
  },
};
